import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Grid, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { SectionHeader } from '@react/components/common/SectionHeader'
import EditField from '@react/components/elements/EditField'
import { useOtChainState } from '@react/redux/store'
import { prodAssetChains } from '@shared/DeploymentConfigsDefaults'

import { LiquidityAssetCTO, LiquidityAssetSchema } from '@shared/LiquidityAssetCTO'
import { logOT } from '@shared/loggingShared'
import axios from 'axios'
import { ethers } from 'ethers'
import React from 'react'
import { useForm } from 'react-hook-form'

export default function LiquidityAssetForm({
  liquidityAsset,
  onSave,
  onCancel,
  editMode = false,
}: {
  liquidityAsset: LiquidityAssetCTO
  onSave: (data: LiquidityAssetCTO) => Promise<void>
  onCancel?: () => void
  editMode?: boolean
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<LiquidityAssetCTO>({
    defaultValues: {
      ...liquidityAsset,
      uuid: liquidityAsset.uuid || ethers.uuidV4(ethers.randomBytes(16)),
      chain: liquidityAsset.chain || '',
      chainId: liquidityAsset.chainId ?? null,
      decimals: liquidityAsset.decimals ?? null,
    },
    resolver: yupResolver(LiquidityAssetSchema),
    mode: 'onSubmit',
  })

  const [formError, setFormError] = React.useState<string | null>(null)
  const [fetchingToken, setFetchingToken] = React.useState(false)
  const [tokenError, setTokenError] = React.useState<string | null>(null)

  const selectedChainId = watch('chainId')
  const address = watch('address')

  const { stageConfiguration } = useOtChainState()

  const { restAPIKey, serverApiBaseUrl, supportedChains = prodAssetChains } = stageConfiguration || {}

  /*const stage = process.env.stage as StageName;
  const stageConfig = findStageConfiguration(stage);
  console.log("HERE: CONFIG:", stageConfig)
  const supportedChains = stageConfig?.supportedChains || [];*/

  // Auto-fetch token details when chainId and address are set
  React.useEffect(() => {
    const fetchTokenDetails = async () => {
      setTokenError(null)
      if (selectedChainId && address && address.length === 42) {
        setFetchingToken(true)
        const url = `${serverApiBaseUrl}/token-details?chainId=${selectedChainId}&address=${address}`
        const headers = restAPIKey ? { 'Content-Type': 'application/json', 'x-api-key': restAPIKey } : {}
        try {
          const res = await axios.get(url, { headers })
          logOT.info('Token details response:', res)
          const data = res.data
          setValue('symbol', data.symbol)
          setValue('name', data.name)
          setValue('decimals', data.decimals)
          setTokenError(null)
        } catch (err) {
          logOT.error('Error fetching token details:', err)
          setValue('symbol', '')
          setValue('name', '')
          setValue('decimals', 0)
          setTokenError(
            `Could not fetch token details: ${(err as any)?.response?.data?.error || (err as any)?.message || 'Unknown error'}`
          )
        } finally {
          setFetchingToken(false)
        }
      } else {
        setValue('symbol', '')
        setValue('name', '')
        setValue('decimals', 0)
      }
    }
    fetchTokenDetails()
  }, [selectedChainId, address, setValue])

  const onSubmit = async (data: LiquidityAssetCTO) => {
    setFormError(null)
    logOT.info('Form data:', data)
    logOT.info('Form errors:', errors)
    data.timestamp = Math.floor(Date.now() / 1000)
    try {
      await onSave(data)
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save asset. Please check your input.'
      setFormError(message)
    }
  }

  return (
    <Box>
      {formError && <div style={{ color: 'red', marginBottom: 8 }}>{formError}</div>}
      {fetchingToken && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Fetching token details…</Typography>
        </Box>
      )}
      {/* Debug: Show current errors */}
      {Object.keys(errors).length > 0 && (
        <Box sx={{ color: 'red', mb: 2, p: 1, border: '1px solid red', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            Form Errors:
          </Typography>
          {Object.entries(errors).map(([field, error]) => (
            <Typography key={field} variant="body2">
              {field}: {error?.message}
            </Typography>
          ))}
        </Box>
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <SectionHeader>Liquidity Asset</SectionHeader>
        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          {editMode ? (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Chain</Typography>
                <Typography>{liquidityAsset.chain}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Chain ID</Typography>
                <Typography>{liquidityAsset.chainId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Address</Typography>
                <Tooltip title={liquidityAsset.address}>
                  <Typography
                    noWrap
                    style={{
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    }}
                  >
                    {liquidityAsset.address}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Symbol</Typography>
                <Typography>{liquidityAsset.symbol}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Name</Typography>
                <Typography>{liquidityAsset.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Decimals</Typography>
                <Typography>{liquidityAsset.decimals}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={liquidityAsset}
                  fieldSchema={LiquidityAssetSchema.describe().fields.exchangeRate}
                />
              </Grid>
            </>
          ) : (
            <>
              {/* Chain Dropdown */}
              <Grid item xs={12} sm={6}>
                <TextField
                  id="chain-select"
                  name="chainId"
                  select
                  label="Chain"
                  value={watch('chainId') || ''}
                  fullWidth
                  required
                  disabled={fetchingToken}
                  SelectProps={{
                    labelId: 'chain-label',
                  }}
                  InputLabelProps={{
                    id: 'chain-label',
                    htmlFor: 'chain-select',
                    shrink: true,
                  }}
                  onChange={(e) => {
                    const chain = supportedChains.find((c) => c.chainID === Number(e.target.value))
                    if (chain) {
                      setValue('chain', chain.chain)
                      setValue('chainId', chain.chainID)
                    } else {
                      setValue('chain', '')
                      setValue('chainId', null as unknown as number)
                    }
                  }}
                  error={!!errors.chain}
                  helperText={errors.chain?.message}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="" disabled>
                    Select Chain
                  </MenuItem>
                  {supportedChains.map((option) => (
                    <MenuItem key={option.chainID} value={option.chainID}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                {/* Hidden input for chainId */}
                <input type="hidden" {...register('chainId')} value={watch('chainId') || ''} />
              </Grid>
              {/* Chain ID (read-only) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  id="chain-id"
                  label="Chain ID"
                  value={watch('chainId') || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  autoComplete="off"
                />
              </Grid>
              {/* Address Input */}
              <Grid item xs={12} sm={6}>
                <TextField
                  //id="token-address"
                  label="Token Address"
                  {...register('address', { required: 'Token Address is required' })}
                  fullWidth
                  required
                  autoComplete="off"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  disabled={fetchingToken}
                />
              </Grid>
              {/* Symbol (read-only) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  id="symbol"
                  label="Symbol"
                  value={watch('symbol') || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  autoComplete="off"
                  error={!!errors.symbol}
                  helperText={errors.symbol?.message}
                />
              </Grid>
              {/* Name (read-only) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  id="name"
                  label="Name"
                  value={watch('name') || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  autoComplete="off"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              {/* Decimals (read-only) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  id="decimals"
                  label="Decimals"
                  value={watch('decimals') || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  autoComplete="off"
                  error={!!errors.decimals}
                  helperText={errors.decimals?.message}
                />
              </Grid>
              {/* Exchange Rate (editable) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  id="exchange-rate"
                  label="Exchange Rate"
                  type="number"
                  inputProps={{ step: 'any' }}
                  {...register('exchangeRate', {
                    required: 'Exchange Rate must be non-negative',
                    min: { value: 0, message: 'Exchange Rate must be non-negative' },
                  })}
                  fullWidth
                  required
                  autoComplete="off"
                  error={!!errors.exchangeRate}
                  helperText={errors.exchangeRate?.message}
                />
              </Grid>
              {/* Deposit Address (editable) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Deposit Address"
                  {...register('depositAddress', { required: 'Deposit Address is required' })}
                  fullWidth
                  required
                  autoComplete="off"
                  error={!!errors.depositAddress}
                  helperText={errors.depositAddress?.message}
                />
              </Grid>
              {/* Reserver transfer Address (editable) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reserver Transfer Address"
                  {...register('reserveTransferAddress', { required: 'Reserver Transfer Address is required' })}
                  fullWidth
                  required
                  autoComplete="off"
                  error={!!errors.reserveTransferAddress}
                  helperText={errors.reserveTransferAddress?.message}
                />
              </Grid>
              {/* Token fetch error */}
              {tokenError && (
                <Grid item xs={12}>
                  <Typography color="error">{tokenError}</Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {onCancel && (
            <Button onClick={onCancel} sx={{ mr: 2 }}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={fetchingToken}>
            Save
          </Button>
        </Box>
      </form>
    </Box>
  )
}
